import { useEffect, useId, useRef, useState } from 'react';
import { useTheme } from 'next-themes';
import mermaid from 'mermaid';

interface MermaidDiagramProps {
    mermaidCode: string;
    caption?: string;
}

export function getMermaidThemeVariables(isDark: boolean): Record<string, string> {
    return {
        background: 'transparent',
        primaryColor: isDark ? '#1f2937' : '#F9F7F7',
        primaryTextColor: isDark ? '#F9F7F7' : '#000000',
        primaryBorderColor: '#0470f0',
        lineColor: isDark ? '#4895ef' : '#0470f0',
        fontFamily: 'inherit',
    };
}

export default function MermaidDiagram({ mermaidCode, caption }: MermaidDiagramProps) {
    const diagramId = useId().replace(/[^a-zA-Z0-9]/g, '');
    const containerRef = useRef<HTMLDivElement>(null);
    const { resolvedTheme } = useTheme();
    const [hasRenderError, setHasRenderError] = useState(false);
    // Center the diagram when it fits within its container; keep it left-aligned when it's
    // wider than the container and needs horizontal scroll (see comment on the wrapper below
    // for why centering an overflowing diagram is worse than left-aligning it).
    const [shouldCenter, setShouldCenter] = useState(false);

    useEffect(() => {
        let isMounted = true;

        mermaid.initialize({
            startOnLoad: false,
            theme: 'base',
            themeVariables: getMermaidThemeVariables(resolvedTheme === 'dark'),
            // useMaxWidth: false renders diagrams at their true, readable pixel size instead
            // of shrinking the whole SVG (text included) to fit the container. Wide diagrams
            // scroll horizontally via the overflow-x-auto wrapper below instead of shrinking.
            flowchart: { useMaxWidth: false, htmlLabels: true },
        });

        mermaid
            .renderAsync(`mermaid-diagram-${diagramId}`, mermaidCode)
            .then((svg) => {
                if (isMounted && containerRef.current) {
                    containerRef.current.innerHTML = svg;

                    // Belt-and-suspenders: don't rely solely on the `useMaxWidth: false` config
                    // above actually being honored. Force the rendered SVG to its own intrinsic
                    // (viewBox) size directly, and strip any width/style mermaid may have set to
                    // shrink it to the container - so wide diagrams scroll horizontally at full,
                    // readable text size instead of shrinking to fit.
                    const svgEl = containerRef.current.querySelector('svg');
                    const viewBox = svgEl?.getAttribute('viewBox');
                    const dimensions = viewBox?.split(/\s+/).map(Number);
                    if (svgEl && dimensions && dimensions.length === 4 && !dimensions.some(Number.isNaN)) {
                        const [, , viewBoxWidth, viewBoxHeight] = dimensions;
                        svgEl.setAttribute('width', String(viewBoxWidth));
                        svgEl.setAttribute('height', String(viewBoxHeight));
                        svgEl.style.removeProperty('max-width');
                        svgEl.style.width = `${viewBoxWidth}px`;
                        // The wrapper below is a flex row, and flex items shrink to fit their
                        // container by default - even ones with an explicit width - unless told
                        // not to. That silently compressed the whole SVG (text included) even
                        // after the width/height above were set correctly. flexShrink: 0 is the
                        // actual fix: it lets the SVG stay at full, readable size and overflow
                        // into the horizontal scrollbar instead of being squeezed to fit.
                        svgEl.style.flexShrink = '0';
                    }

                    // Now that the SVG has its true intrinsic size, decide alignment: if it
                    // fits without overflowing, center it for a tidier look; if it's wider than
                    // the container, leave it left-aligned so the scrollbar starts at the first
                    // node instead of the middle of the diagram.
                    const el = containerRef.current;
                    if (el) {
                        setShouldCenter(el.scrollWidth <= el.clientWidth);
                    }
                }
            })
            .catch((error: unknown) => {
                console.error('Failed to render mermaid diagram', error);
                if (isMounted) {
                    setHasRenderError(true);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [mermaidCode, diagramId, resolvedTheme]);

    if (hasRenderError) {
        return null;
    }

    return (
        <div className="my-6 rounded-r-lg border-l-4 border-darkBlue bg-gray-50 p-6 dark:border-lightBlue dark:bg-gray-800/30">
            {/*
             * Centered when the diagram fits its container (shouldCenter, set after render by
             * comparing scrollWidth to clientWidth). Left-aligned instead when the diagram is
             * wider than its container, so the horizontal scroll starts at the beginning of the
             * flow (its first node) rather than centered on the middle of a diagram the reader
             * hasn't seen yet. [&>svg]:shrink-0 stops the flex row from compressing the SVG to
             * fit - without it, a flex item shrinks below its own explicit width by default,
             * which is what was silently squeezing every diagram's text down to an unreadable
             * size.
             */}
            <div
                ref={containerRef}
                className={`flex overflow-x-auto [&>svg]:shrink-0 ${shouldCenter ? 'justify-center' : 'justify-start'}`}
            />
            {caption && (
                <div className="mt-3 text-center text-sm italic text-darkBlue dark:text-lightBlue">{caption}</div>
            )}
        </div>
    );
}
